import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { decodeToken } from "react-jwt";
import { setAuthToken } from "../AxiosFunctions/setCommonHeader";
import { updateMessage, updateOpen } from "../redux/message";
import {
  getSingleAgent,
  getSingleClient,
  getSingleArtist,
} from "../AxiosFunctions/Axiosfunctionality";
import {
  isValidToken,
  getUserFromToken,
  removeTokenFromUrl,
  getTokenFromUrl,
} from "../utlils/tokenUtils";
import loading from "../assets/loading.gif";

const TokenVerifier = ({ children }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const verifyTokenFromURL = async () => {
      // Check if there's a token in the URL
      const token = getTokenFromUrl();

      if (!token) {
        setIsVerified(true);
        return;
      }

      // Validate token format
      if (!isValidToken(token)) {
        dispatch(updateOpen(true));
        dispatch(updateMessage("Invalid token format"));
        removeTokenFromUrl(history);
        setIsVerified(true);
        return;
      }

      setIsVerifying(true);

      try {
        // Get user information from token
        const userInfo = getUserFromToken(token);
        if (!userInfo) {
          throw new Error("Invalid token");
        }

        // Set the token in localStorage and axios headers
        localStorage.setItem("authorization", token);
        setAuthToken(token);

        // Determine user type and fetch user details from backend
        let userDetails = null;
        let userType = null;

        // Try to get agent details
        try {
          const agentResponse = await getSingleAgent({ _id: userInfo.id });
          if (agentResponse && agentResponse !== "NO AGENT FOUND") {
            userDetails = agentResponse;
            userType = "agent";
            localStorage.setItem(
              "agentInfo",
              JSON.stringify({ _id: agentResponse._id })
            );
            localStorage.setItem("popUpMyShannon", "0");
          }
        } catch (error) {
          console.log("Not an agent user");
        }

        // If not agent, try client
        if (!userDetails) {
          try {
            const clientResponse = await getSingleClient({ _id: userInfo.id });
            if (clientResponse && clientResponse !== "NO CLIENT FOUND") {
              userDetails = clientResponse;
              userType = "client";
            }
          } catch (error) {
            console.log("Not a client user");
          }
        }

        // If we found a user, redirect appropriately
        if (userDetails) {
          dispatch(updateOpen(true));
          dispatch(updateMessage("Successfully logged in via token"));

          // Remove token from URL
          removeTokenFromUrl(history);

          if (
            userType !== "artist" &&
            userType !== "client" &&
            userType !== "agent"
          ) {
            // No valid user found
            localStorage.removeItem("authorization");
            setAuthToken(null);
            dispatch(updateOpen(true));
            dispatch(updateMessage("Invalid token or user not found"));

            // Remove token from URL
            removeTokenFromUrl(history);
          }
        } else {
          // No valid user found
          localStorage.removeItem("authorization");
          setAuthToken(null);
          dispatch(updateOpen(true));
          dispatch(updateMessage("Invalid token or user not found"));

          // Remove token from URL
          removeTokenFromUrl(history);
        }
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("authorization");
        setAuthToken(null);
        dispatch(updateOpen(true));
        dispatch(updateMessage("Invalid token"));

        // Remove token from URL
        removeTokenFromUrl(history);
      } finally {
        setIsVerifying(false);
        setIsVerified(true);
      }
    };

    verifyTokenFromURL();
  }, [location.search, history, dispatch]);

  if (isVerifying) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <img
            src={loading}
            alt="Verifying token..."
            style={{ width: "50px", marginBottom: "10px" }}
          />
          <p>{/* Verifying your login... */}</p>
        </div>
      </div>
    );
  }

  return isVerified ? children : null;
};

export default TokenVerifier;
